<template>
    <AppLayout :loading="processing">
        <div class="max-w-7xl mx-auto px-4 py-6">
            <div>
                <!-- Page Title -->
                <div class="mb-6">
                    <h1 class="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">
                        {{ $tc('LBL_BRAND', 2) }}
                    </h1>
                    <!-- Breadcrumb -->
                    <nav
                        v-if="isAdmin"
                        class="text-sm text-gray-500 mb-6 dark:text-gray-300"
                        aria-label="Breadcrumb"
                    >
                        <ol class="flex items-center space-x-2">
                            <li class="flex items-center">
                                <inertia-link
                                    :href="route('landing')"
                                    class="text-green-600 hover:underline"
                                >
                                    {{ $t('LBL_HOME') }}
                                </inertia-link>
                                <svg
                                    class="w-3 h-3 mx-2 text-green-400"
                                    fill="currentColor"
                                    viewBox="0 0 320 512"
                                >
                                    <path
                                        d="M285.476 272.971L91.132 467.314c-9.373 9.373-24.569 9.373-33.941 0l-22.667-22.667c-9.357-9.357-9.375-24.522-.04-33.901L188.505 256 34.484 101.255c-9.335-9.379-9.317-24.544.04-33.901l22.667-22.667c9.373-9.373 24.569-9.373 33.941 0L285.475 239.03c9.373 9.372 9.373 24.568.001 33.941z"
                                    />
                                </svg>
                            </li>
                            <li class="text-gray-700">
                                {{ $tc('LBL_BRAND', 1) }}
                            </li>
                        </ol>
                    </nav>
                </div>
                <!-- breadcrumb end -->

                <div>
                    <div
                        v-if="isAdmin"
                        class="w-full bg-white dark:bg-gray-900 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 overflow-overflow"
                    >
                        <!-- Search & Action Bar -->
                        <div class="flex flex-wrap items-center justify-between gap-4 p-4 sm:flex-nowrap">
                            <!-- Search Box -->
                            <div class="relative w-full sm:w-auto">
                                <label
                                    for="table-search"
                                    class="sr-only"
                                    >Search</label
                                >
                                <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <svg
                                        class="w-5 h-5 text-gray-500 dark:text-gray-400"
                                        fill="currentColor"
                                        viewBox="0 0 20 20"
                                    >
                                        <path
                                            fill-rule="evenodd"
                                            d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                                            clip-rule="evenodd"
                                        />
                                    </svg>
                                </div>
                                <t-input
                                    id="category-search-box"
                                    v-model="search"
                                    type="text"
                                    variant="success"
                                    class="w-full sm:w-80 pl-10 pr-4 py-2.5 text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-green-500 focus:outline-none dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                                    placeholder="Search for brand"
                                    @keypress.enter="fetchPageData(brands.data.current_page)"
                                />
                            </div>

                            <!-- Action Buttons -->
                            <div class="flex flex-wrap">
                                <t-button
                                    class="inline-flex items-center rounded-md"
                                    variant="success"
                                    @click="toggleRightSidebar"
                                >
                                    <span>Add Brand</span>
                                </t-button>
                            </div>
                        </div>

                        <div
                            class="w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-none shadow-md"
                        >
                            <div class="overflow-x-auto">
                                <!-- Table content to list the Products -->
                                <table
                                    class="min-w-[700px] w-full table-auto text-sm sm:text-base text-left text-gray-700 dark:text-gray-300"
                                >
                                    <thead class="bg-gray-100 dark:bg-gray-700 text-sm font-semibold">
                                        <tr>
                                            <th class="px-4 py-3">#</th>
                                            <th class="px-4 py-3">IMAGE</th>
                                            <th class="px-4 py-3">NAME</th>
                                            <th class="px-4 py-3">PRODUCT COUNT</th>
                                            <th class="px-4 py-3">ACTIONS</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <template v-for="(row, rowIndex) in brands.data">
                                            <tr>
                                                <td class="px-4 py-3">
                                                    {{ rowIndex + 1 + (currentPage - 1) * perPage }}
                                                </td>
                                                <td class="px-4 py-3 h-20">
                                                    <img
                                                        v-if="row.images?.length"
                                                        class="object-contain h-full"
                                                        :src="row.images[0].image_path"
                                                        :alt="row.name"
                                                    />
                                                    <img
                                                        v-else
                                                        :src="`https://ui-avatars.com/api/?name=${row.name}&color=7F9CF5&background=EBF4FF`"
                                                        class="object-contain h-full"
                                                        :alt="row.name"
                                                    />
                                                </td>
                                                <td class="px-4 py-3">
                                                    {{ row.name }}
                                                </td>
                                                <td class="px-4 py-3">
                                                    {{ row.products_count || 0 }}
                                                </td>
                                                <td class="flex items-center px-4 py-3">
                                                    <t-button
                                                        variant="group"
                                                        size="sm"
                                                        class="rounded-none rounded-l-md"
                                                        @click="edit(row.id)"
                                                    >
                                                        <span>Edit</span>
                                                    </t-button>
                                                    <t-button
                                                        variant="groupwarn"
                                                        size="sm"
                                                        class="rounded-none rounded-r-md"
                                                        @click="deleteItem(row.id)"
                                                    >
                                                        <span>Delete</span>
                                                    </t-button>
                                                </td>
                                            </tr>
                                        </template>
                                        <tr
                                            v-if="!brands?.data.length"
                                            class="text-center"
                                        >
                                            <td
                                                colspan="6"
                                                class="px-4 py-4 text-gray-500 dark:text-gray-400"
                                            >
                                                {{ $t('LBL_DATA_FOUND') }}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <div
                                v-if="brands?.data.length"
                                class="flex justify-center items-center bg-gray-100 dark:bg-gray-800 px-4 py-4 border-t dark:border-gray-700"
                            >
                                <t-pagination
                                    v-model="currentPage"
                                    variant="rounded"
                                    :total-items="totalRows"
                                    :per-page="perPage"
                                    :limit="limit"
                                    :disabled="isBrandListEmpty"
                                    @change="fetchPageData"
                                />
                            </div>
                            <p class="sm:hidden text-xs text-center text-gray-500 py-2">
                                ↔ Swipe left/right to scroll the table
                            </p>
                        </div>
                    </div>
                </div>

                <div v-if="!isAdmin">
                    <div class="flex items-center justify-around">
                        <div
                            class="text-lg font-bold text-blue-400 cursor-pointer hover:text-blue-600"
                            :class="filterByAlpha === null ? 'text-blue-800' : ''"
                            @click="goto('brands.index')"
                        >
                            All
                        </div>
                        <div
                            v-for="pos in 26"
                            :key="'alphabet-' + pos"
                            class="text-lg font-bold text-blue-400 cursor-pointer hover:text-blue-600"
                            :class="filterByAlpha === String.fromCharCode(pos + 64) ? 'text-blue-800' : ''"
                            @click="goto('brands.index', { filterByAlpha: String.fromCharCode(pos + 64) })"
                        >
                            {{ String.fromCharCode(pos + 64) }}
                        </div>
                    </div>

                    <!-- Table content to list the Brands for Retailer -->
                    <div class="mt-4">
                        <div
                            v-if="!isBrandListEmpty"
                            class="grid grid-cols-2 gap-4 md:grid-cols-7"
                        >
                            <div
                                v-for="brand in brands.data"
                                :key="brand.id"
                                class="cursor-pointer"
                                @click="goto('products.index', { brandIds: [brand.id] })"
                            >
                                <div class="mb-4 overflow-hidden border border-gray-200 rounded-md h-44">
                                    <img
                                        :src="
                                            brand.images?.length
                                                ? brand.images[0].image_path
                                                : `https://ui-avatars.com/api/?name=${brand.name}&color=7F9CF5&background=EBF4FF`
                                        "
                                        alt=""
                                        class="w-full h-5/6"
                                    />
                                </div>

                                <p class="font-semibold text-center text-gray-500">
                                    {{ brand.name }}
                                </p>
                            </div>
                        </div>

                        <div v-else>No Brands.</div>
                    </div>
                </div>
            </div>

            <RightSidebar
                :is-open="rightSidebar"
                :component-name="rightSidebarComponentName"
                :edit-data="editData"
                @closeRightSidebar="toggleRightSidebar"
                @set-processing="processing = $event"
            >
                <template #heading>
                    <!-- Title -->
                    <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div class="flex items-center gap-3 min-w-0 flex-1">
                            <h2 class="text-xl font-semibold text-gray-800 dark:text-white">Brand Information</h2>
                        </div>
                        <t-button
                            variant="text"
                            class="hover:text-red-500"
                            @click="rightSidebar = false"
                        >
                            ✕ Close
                        </t-button>
                    </div>
                </template>
            </RightSidebar>
        </div>
    </AppLayout>
</template>

<script>
    import AppLayout from '../../Layouts/AppLayout.vue';
    import RightSidebar from '../../Components/RightSidebar.vue';
    import CreateIcon from '../../Components/Icons/CreateIcon.vue';
    import EditIcon from '../../Components/Icons/EditIcon.vue';
    import DeleteIcon from '../../Components/Icons/DeleteIcon.vue';
    import BreadCrumbs from '../../Components/BreadCrumbs.vue';

    import Button from '../../Components/Button.vue';

    export default {
        name: 'BrandsIndex',

        components: {
            AppLayout,
            RightSidebar,
            CreateIcon,
            EditIcon,
            DeleteIcon,
            Button,
            BreadCrumbs,
        },

        props: {
            brands: {
                type: [Object, Array],
                required: true,
            },

            filterByAlpha: {
                type: String,
                required: false,
                default: null,
            },
        },

        data() {
            return {
                processing: false,
                rightSidebar: false,
                rightSidebarComponentName: 'CreateBrand',
                editData: null,
                currentPage: null,
                limit: 5,
                perPage: this.brands.data.per_page,
                totalRows: this.brands.data.total,
                breadCrumbs: [{ title: 'Brands' }],
                search: null,
            };
        },

        computed: {
            isBrandListEmpty() {
                // this means the brands prop is a object and is paginated
                if (!!this.brands.data && !!this.brands.data.length) {
                    return false;
                }
                // this means the brands prop is a array
                if (this.brands.length) {
                    return false;
                }
                return true;
            },
        },

        watch: {
            rightSidebar(newVal) {
                if (newVal === false) {
                    this.editData = null;
                }
            },

            brands: {
                deep: true,
                handler() {
                    if (this.editData) {
                        this.editData = this.brands.data.find((brand) => brand.id === this.editData.id);
                    }
                },
            },
        },

        created() {
            this.currentPage = this.brands.data.current_page;
        },

        methods: {
            toggleRightSidebar() {
                this.rightSidebar = !this.rightSidebar;
            },

            edit(brandId) {
                const brand = this.brands.data.find((item) => item.id === brandId);
                this.editData = brand;
                this.toggleRightSidebar();
            },

            deleteItem(brandId) {
                const brand = this.brands.data.find((item) => item.id === brandId);
                this.$dialog
                    .confirm({
                        title: 'Confirm',
                        text: `Are you sure you want to delete ${brand.name}?`,
                    })
                    .then((result) => {
                        if (result.isOk) {
                            this.$inertia.delete(route('brands.destroy', brand.id));
                        }
                    });
            },

            fetchPageData(newPage) {
                this.$inertia.get(
                    route('brands.index', {
                        page: newPage,
                    }),
                );
            },
        },
    };
</script>
